import React, { ReactNode, useMemo } from 'react'
import {
  Avatar,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import useToken from '@plug-core/hooks/useToken'
import { useSns } from '@plug-core/state'
import useWallet from '@plug-core/hooks/useWallet'
import { formatNumber } from '@plug-core/utils/formatNumber'
import { solanaClient } from '@plug-core/clients/SolanaClient'
import { TransactionType } from '@plug-core/interfaces/common'
import { TransactionActivity } from '@plug-core/interfaces/transactionActivity'
import { shortAddress } from '@plug-core/utils/shortAddress'
import { hasOtherParty } from '@plug-core/hooks/useTransactionOtherParty'
import { e8sToDecimals } from '@plug-core/utils/validationHelper'
import Button from '@components/Button'
import { TransactionOtherParty } from './TransactionOtherParty'

const DASHBOARD_BASE_URL = 'https://dashboard.internetcomputer.org'

export const TransactionDetails = () => {
  const { transactionId, tokenSymbol, tokenCanisterId } = useParams<{
    transactionId: string
    tokenSymbol: string
    tokenCanisterId: string
  }>()

  const {
    state: { transaction },
  } = useLocation() as { state: { transaction: TransactionActivity } }

  const { logo, decimals } = useToken(transaction.tokenLedgerId)
  const { sns } = useSns()
  const { t } = useTranslation()
  const { wallet } = useWallet()

  const renderTransactionAmount = ({ type, amount }: TransactionActivity) => {
    const prefix = type === TransactionType.Send ? '-' : '+'

    return `${prefix}${formatNumber(e8sToDecimals(amount, decimals || 8))}`
  }

  const renderListItem = (label: string, value: ReactNode | undefined) => (
    <ListItem>
      <ListItemText secondary={label} />
      <ListItemText
        primary={value}
        primaryTypographyProps={{ fontWeight: '600 !important' }}
        sx={{ textAlign: 'right' }}
      />
    </ListItem>
  )

  const dashboardUrl = useMemo(() => {
    if (transaction.chain === 'ETH') {
      return `https://etherscan.io/tx/${transaction.transactionId}`
    }

    if (transaction.chain === 'ARB') {
      return `https://arbiscan.io/tx/${transaction.transactionId}`
    }

    if (transaction.chain === 'BASE') {
      return `https://basescan.org/tx/${transaction.transactionId}`
    }

    if (tokenSymbol === 'ICP') return `${DASHBOARD_BASE_URL}/transaction/${transactionId}`
    if (tokenSymbol === 'ckBTC') return `${DASHBOARD_BASE_URL}/bitcoin/transaction/${transactionId}`
    if (tokenSymbol === 'ckETH')
      return `${DASHBOARD_BASE_URL}/ethereum/transaction/${transactionId}`

    const currentSns = sns.find((sns) => sns.symbol === tokenSymbol)
    if (currentSns)
      return `${DASHBOARD_BASE_URL}/sns/${currentSns?.rootCanisterId}/transaction/${transactionId}`

    const ethereumTokens = [
      'ckUSDC',
      'ckUSDT',
      'ckEURC',
      'ckWBTC',
      'ckWSTETH',
      'ckXAUT',
      'ckOCT',
      'ckPEPE',
      'ckSHIB',
      'ckLINK',
      'ckUNI',
    ]
    if (ethereumTokens.includes(tokenSymbol || '')) {
      return `${DASHBOARD_BASE_URL}/ethereum/${tokenCanisterId}/transaction/${transactionId}`
    }

    if (transaction.chain === 'BTC') {
      return `https://www.blockchain.com/explorer/transactions/btc/${transaction.transactionId}`
    }

    if (transaction.chain === 'SOL') {
      return solanaClient.transactionExploreUrl(transaction.transactionId.toString())
    }
  }, [transactionId, tokenSymbol, tokenCanisterId, sns, wallet.principalId])

  return (
    <Stack spacing={3} mx={3}>
      <Stack alignItems="center" spacing={2} pb={2}>
        <Box
          component={Avatar}
          width={86}
          height={86}
          bgcolor="background.paper"
          boxShadow="0 4px 10px rgba(0,0,0,0.3)"
          src={logo}
        >
          {transaction.currency?.slice(0, 2)}
        </Box>
        <Typography
          variant="h2"
          textAlign="center"
          color={transaction.type === 'receive' ? 'success.light' : 'grey.300'}
          fontSize={24}
        >
          {renderTransactionAmount(transaction)}
          <Typography fontSize={18} fontWeight="bold" color="text.secondary">
            {tokenSymbol}
          </Typography>
        </Typography>
      </Stack>

      <Box component={List} borderRadius={1} bgcolor="background.paper" disablePadding>
        {renderListItem(
          t('transactionHistory.details.transactionId'),
          shortAddress(transaction.transactionId.toString(), {
            rightSize: 7,
            leftSize: 7,
          })
        )}
        <Divider sx={{ borderColor: 'background.default' }} />

        {renderListItem(
          t('transactionHistory.details.date'),
          format(transaction.timestamp, 'd MMMM yyyy')
        )}
        <Divider sx={{ borderColor: 'background.default' }} />

        {renderListItem(
          t('transactionHistory.details.time'),
          format(transaction.timestamp, 'HH:mm:ss')
        )}

        {hasOtherParty(transaction) && (
          <>
            <Divider sx={{ borderColor: 'background.default' }} />

            {transaction.type === TransactionType.Send
              ? renderListItem(
                  t('transactionHistory.details.to'),
                  <TransactionOtherParty transaction={transaction} long copy />
                )
              : renderListItem(
                  t('transactionHistory.details.from'),
                  <TransactionOtherParty transaction={transaction} long copy />
                )}
          </>
        )}

        {transaction.memo && (
          <>
            <Divider sx={{ borderColor: 'background.default' }} />
            {renderListItem(t('transactionHistory.details.memo'), transaction.memo)}
          </>
        )}
      </Box>

      {dashboardUrl && (
        <Box
          component={Button}
          fontWeight="bold"
          fontSize="1rem"
          py={1.5}
          borderRadius={2}
          color="white"
          sx={{
            background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
          }}
          fullWidth
          onClick={() => window.open(dashboardUrl)}
        >
          {t('transactionHistory.details.viewDetails')}
        </Box>
      )}
    </Stack>
  )
}
